// features/timesheet/timesheet.service.ts
import { get, post, put } from '../../api/api.service';

export interface TimesheetWorkingHoursV2 {
  id: number;
  timeSheetWorkingHourId?: number;
  articleNumber: string;
  workingDate: string;
  customerId?: number;
  workingHour: number;
  unit: string;
  amountPerUnit: number;
  amountPerUnitSek?: number;
  number: number;
  houseworkType?: string;
  userId: number;
  workingDescription: string;
  customWorkingDescription: string;
  isActive?: boolean;
  freelancerName?: string;
  dateId: string;
  isTaxFreeArticle?: boolean;
  isArticleShownInInvoice: boolean;
  isPdfAttachedToInvoice?: boolean;
  tripStartedPlace?: string;
  tripEndedPlace?: string;
  distance?: number;
  country?: string;
  allowancePerDay?: number;
  reasonForTrip?: string;
  carRegistrationNo?: string;
  vat: number;
  invoiceCustomerVat?: number;
  tripStartedDate?: string;
  tripEndedDate?: string;
  amount: number;
  foodAllowance?: number;
  amountInSek?: number;
  totalDayOfTravels?: number;
  allowanceDestination?: string;
  description?: string;
  expenseCategory?: string;
  fileName?: string;
  bookkeepingArticleNumber?: number;
  discount?: number;
  discountType?: number;
  amountWithoutDiscount?: number;
}

export interface AddTimesheetRequest {
  customerId: number;
  timeSheetWorkingHoursV2: TimesheetWorkingHoursV2[];
}

export interface AddTimesheetResponse {
  statusCode: number;
  message: string;
  response: any;
  totalCount: number;
  numberOfPages: number;
}

export const addTimesheetWorkingHoursApi = async (
  data: AddTimesheetRequest
): Promise<AddTimesheetResponse> => {
  const response = await post('/timesheetv2/add-timesheet-workinghours', data);
  return response as AddTimesheetResponse;
};

export interface GetTimesheetRequest {
  fromDate: string;
  toDate: string;
  Month?: string;
  year?: string;
  customerId: string;
  pageNumber?: number;
  tab?: number;
}

export interface GetTimesheetResponse {
  totalAmount?: number;
  totalAmountIncVat?: number;
  timeSheetWorkingHoursV2List: TimesheetWorkingHoursV2[];
  totalCount?: number;
  numberOfPages?: number;
}

export const getTimesheetWorkingHoursApi = async (
  params: GetTimesheetRequest
): Promise<GetTimesheetResponse> => {
  const queryParams: Record<string, string> = {
    fromDate: params.fromDate,
    toDate: params.toDate,
    customerId: params.customerId,
  };
  
  // Only add Month and year if they are provided
  if (params.Month) {
    queryParams.Month = params.Month;
  }
  if (params.year) {
    queryParams.year = params.year;
  }
  
  // Add pageNumber if provided
  if (params.pageNumber !== undefined) {
    queryParams.pageNumber = params.pageNumber.toString();
  }
  
  // Add tab if provided
  if (params.tab !== undefined) {
    queryParams.tab = params.tab.toString();
  }
  
  const searchParams = new URLSearchParams(queryParams);
  const response = await get(`/timesheetv2/get-timesheet-workinghours?${searchParams.toString()}`);
  return response as GetTimesheetResponse;
};

/**
 * Fetches timesheet records for the Timesheet Creation page.
 * Corresponds to: GET /deductionsv2/get-timesheet-deductions?tab=1
 */
export const getTimesheetCreationApi = async (
  params: GetTimesheetRequest
): Promise<GetTimesheetResponse> => {
  const queryParams: Record<string, string> = {
    fromDate: params.fromDate,
    toDate: params.toDate,
    customerId: params.customerId,
    tab: '1',
  };

  if (params.pageNumber !== undefined) {
    queryParams.pageNumber = params.pageNumber.toString();
  }

  const searchParams = new URLSearchParams(queryParams);
  const response = await get(`/deductionsv2/get-timesheet-deductions?${searchParams.toString()}`);
  return response as GetTimesheetResponse;
};

export interface GetEmployeeWorkingHoursRequest {
  fromDate: string;
  toDate: string;
}

export interface EmployeeWorkingHoursData {
  serialNo: string;
  userId: string;
  absences: string;
  hours: string;
  issuedDate: string;
}

export interface GetEmployeeWorkingHoursResponse {
  statusCode: number;
  message: string;
  response: EmployeeWorkingHoursData[];
  totalCount: number;
  numberOfPages: number;
}

export const getEmployeeWorkingHoursApi = async (
  params: GetEmployeeWorkingHoursRequest
): Promise<GetEmployeeWorkingHoursResponse> => {
  const queryParams = new URLSearchParams({
    fromDate: params.fromDate,
    toDate: params.toDate,
  });
  const response = await get(`/employeecertificate/get-working-hours?${queryParams.toString()}`);
  return response as GetEmployeeWorkingHoursResponse;
};

export interface DeleteTimesheetRequest {
  timeSheetWorkingHourIds: number[];
}

export interface DeleteTimesheetResponse {
  statusCode: number;
  message: string;
  response: any;
  totalCount: number;
  numberOfPages: number;
}

export const deleteTimesheetWorkingHoursApi = async (
  data: DeleteTimesheetRequest
): Promise<DeleteTimesheetResponse> => {
  const response = await put('/timesheetv2/delete-timesheet-workinghours', data.timeSheetWorkingHourIds);
  return response as DeleteTimesheetResponse;
};

export interface UpdateTimesheetRequest {
  customerId: number;
  timeSheetWorkingHoursV2: TimesheetWorkingHoursV2[];
}

export interface UpdateTimesheetResponse {
  statusCode: number;
  message: string;
  response: any;
  totalCount: number;
  numberOfPages: number;
}

export const updateTimesheetWorkingHoursApi = async (
  data: UpdateTimesheetRequest
): Promise<UpdateTimesheetResponse> => {
  const response = await put('/deductionsv2/update-deductions', data);
  return response as UpdateTimesheetResponse;
};

// Separate API for TimesheetCreation inline edit — uses different endpoint
export const updateTimesheetHoursApi = async (
  data: UpdateTimesheetRequest
): Promise<UpdateTimesheetResponse> => {
  const response = await put('/timesheetv2/update-timesheet-hours', data);
  return response as UpdateTimesheetResponse;
};

export interface GetTimesheetReportRequest {
  fromDate: string;
  toDate: string;
  searchQuery: string;
  pageNumber?: number;
}

export interface TimesheetReportData {
  id: string;
  number: string;
  dateInterval: string;
  clientName: string;
  // Add other fields as needed based on actual API response
}

export interface GetTimesheetReportResponse {
  statusCode: number;
  message: string;
  response: TimesheetReportData[];
  totalCount: number;
  numberOfPages: number;
}

export const getTimesheetReportApi = async (
  params: GetTimesheetReportRequest
): Promise<GetTimesheetReportResponse> => {
  const queryParams: Record<string, string> = {
    fromDate: params.fromDate,
    toDate: params.toDate,
    searchQuery: params.searchQuery,
  };
  
  // Add pageNumber if provided
  if (params.pageNumber !== undefined) {
    queryParams.pageNumber = params.pageNumber.toString();
  }
  
  const searchParams = new URLSearchParams(queryParams);
  const response = await get(`/timesheetv2/get-timesheet-report?${searchParams.toString()}`);
  return response as GetTimesheetReportResponse;
};
